import { useEffect } from 'react'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { setMeIntegrationEnabled } from '@sportninja/common/actions/account'
import { useDispatch } from 'src/hooks/useRedux'
import { useIntegrationsReducer } from './useIntegrationsReducer'

/**
 * Interface for useHockeyUSAIntegration hook parameters
 */
interface HockeyUSAIntegrationParams {
  accountUid?: string
  integrations?: {
    daysmart?: {
      is_connected: boolean
    }
    usahockey?: {
      is_enabled: boolean
    }
  }
}

/**
 * Interface for useHockeyUSAIntegration hook return value
 */
interface HockeyUSAIntegrationReturn {
  // State values
  hockeyUSAApiKey: string
  hockeyUSASecretKey: string
  isHockeyUSAConnected: boolean
  isHockeyUSAEnabled: boolean
  showHockeyUSAWarning: boolean
  pendingHockeyUSAEnabled: boolean

  // Actions
  actions: ReturnType<typeof useIntegrationsReducer>['actions']

  // Functions
  handleHockeyUSAToggle: (enabled: boolean) => Promise<void>
  updateHockeyUSAStatus: (enabled: boolean) => Promise<void>
  confirmHockeyUSAEnable: () => Promise<void>
  cancelHockeyUSAEnable: () => void
}

/**
 * Custom hook for managing USA Hockey integration functionality
 *
 * This hook encapsulates all USA Hockey-related logic including:
 * - Fetching USA Hockey API keys and enabled status from the backend
 * - Toggling USA Hockey integration on/off with proper confirmation flow
 * - Managing the warning modal state for cost confirmation
 * - Handling the enable/disable workflow with proper error handling
 *
 * @param params - Configuration object containing account UID
 * @returns Object containing state values, actions, and utility functions for USA Hockey integration
 *
 * @example
 * ```typescript
 * const hockeyUSAIntegration = useHockeyUSAIntegration({
 *   accountUid: account?.uid
 * })
 *
 * // Access state
 * const { isHockeyUSAEnabled, showHockeyUSAWarning } = hockeyUSAIntegration
 *
 * // Perform actions
 * await hockeyUSAIntegration.handleHockeyUSAToggle(true)
 * hockeyUSAIntegration.cancelHockeyUSAEnable()
 * ```
 */
export const useHockeyUSAIntegration = ({
  accountUid,
  integrations,
}: HockeyUSAIntegrationParams): HockeyUSAIntegrationReturn => {
  console.log(
    '🔄 useHockeyUSAIntegration: Initializing USA Hockey integration hook'
  )

  const dispatch = useDispatch()
  const { state, actions } = useIntegrationsReducer()
  const {
    hockeyUSAApiKey,
    hockeyUSASecretKey,
    isHockeyUSAConnected,
    isHockeyUSAEnabled,
    showHockeyUSAWarning,
    pendingHockeyUSAEnabled,
  } = state

  // Set USA Hockey connection status from integrations data
  useEffect(() => {
    if (!accountUid) {
      return
    }

    // Set connection and enabled status from integrations data
    if (integrations?.usahockey?.is_enabled) {
      actions.setIsHockeyUSAConnected(true)
      actions.setIsHockeyUSAEnabled(true)
      console.log('✅ useHockeyUSAIntegration: USA Hockey is enabled')
    } else {
      actions.setIsHockeyUSAConnected(false)
      actions.setIsHockeyUSAEnabled(false)
      console.log('⚠️ useHockeyUSAIntegration: USA Hockey is not enabled')
    }
    // Only depend on accountUid and integrations to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUid, integrations?.usahockey?.is_enabled])

  /**
   * Updates USA Hockey integration status on the server
   * @param enabled - Whether to enable or disable the integration
   */
  const updateHockeyUSAStatus = async (enabled: boolean) => {
    console.log('🔄 useHockeyUSAIntegration: Updating USA Hockey status', {
      enabled,
    })
    try {
      // Use different endpoints for enable and disable
      const endpoint = enabled
        ? `/account/${accountUid}/integrations/usahockey/enable`
        : `/account/${accountUid}/integrations/usahockey/disable`

      await req(endpoint, {
        method: 'PUT',
      })

      actions.setIsHockeyUSAEnabled(enabled)
      // Persist to the account slice too: this hook's state is local and
      // resets when the card unmounts (e.g. switching settings tabs), which
      // otherwise re-seeded a stale value until a full page reload.
      dispatch(setMeIntegrationEnabled({ key: 'usahockey', enabled }))
      console.log(
        '✅ useHockeyUSAIntegration: USA Hockey status updated successfully'
      )
    } catch (e) {
      const errorMessage = getErrorMessage(e)
      console.error(
        '🚨 useHockeyUSAIntegration: Failed to update USA Hockey status',
        errorMessage
      )
      // Reset switch state on error
      actions.setIsHockeyUSAEnabled(!enabled)
    }
  }

  /**
   * Handles the USA Hockey integration toggle with proper confirmation flow
   * - For enabling: Shows warning modal before proceeding
   * - For disabling: Immediately disables without confirmation
   * @param enabled - Whether to enable or disable the integration
   */
  const handleHockeyUSAToggle = async (enabled: boolean) => {
    console.log('🔄 useHockeyUSAIntegration: Toggling USA Hockey integration', {
      enabled,
    })

    if (enabled) {
      // Show warning modal before enabling
      actions.setPendingHockeyUSAEnabled(true)
      actions.setShowHockeyUSAWarning(true)
    } else {
      // Disable immediately without warning
      await updateHockeyUSAStatus(false)
    }
  }

  /**
   * Confirms the USA Hockey integration enable action
   * Called when user confirms they want to enable the integration despite the cost warning
   */
  const confirmHockeyUSAEnable = async () => {
    console.log('🔄 useHockeyUSAIntegration: Confirming USA Hockey enable')
    actions.setShowHockeyUSAWarning(false)
    await updateHockeyUSAStatus(pendingHockeyUSAEnabled)
    actions.setPendingHockeyUSAEnabled(false)
  }

  /**
   * Cancels the USA Hockey integration enable action
   * Called when user decides not to enable the integration after seeing the cost warning
   */
  const cancelHockeyUSAEnable = () => {
    console.log('🔄 useHockeyUSAIntegration: Cancelling USA Hockey enable')
    actions.resetHockeyUSAWarningState()
    // Switch state will remain unchanged (stays false)
  }

  return {
    // State values
    hockeyUSAApiKey,
    hockeyUSASecretKey,
    isHockeyUSAConnected,
    isHockeyUSAEnabled,
    showHockeyUSAWarning,
    pendingHockeyUSAEnabled,

    // Actions
    actions,

    // Functions
    handleHockeyUSAToggle,
    updateHockeyUSAStatus,
    confirmHockeyUSAEnable,
    cancelHockeyUSAEnable,
  }
}
